<template>
  <Modal header-tag="h3" :show="show" @close="$emit('close-modal')">
    <template #header>
      <VisibleText>Train your model </VisibleText>
    </template>

    <!-- TAB NAV -->
    <div class="tabs">
      <button
        :class="['tab-btn', activeTab === 'train' ? 'active' : '']"
        @click="activeTab = 'train'"
      >
        Train Model
      </button>

      <button
        :class="['tab-btn', activeTab === 'advanced' ? 'active' : '']"
        @click="activeTab = 'advanced'"
      >
        Advanced Settings
      </button>
    </div>

    <!-- TAB 1: TRAIN -->
    <div v-if="activeTab === 'train'" class="tab-content">
      <div style="display: flex; flex-direction: column">
        <span
          v-if="isLoading"
          style="height: 2rem; width: 2rem; margin-top: 1rem"
          class="also-found__loading-spinner spinner"
        ></span>
        <h4>Last Trained Model:</h4>
        <div class="model-status" v-if="hasModel && !isLoading">
          <p><strong>Last trained:</strong> {{ formattedDate }}</p>
          <p>
            <strong>Training artefacts:</strong> {{ modelMeta.n_total }} ({{ modelMeta.n_pos }} pos
            / {{ modelMeta.n_neg }} neg)
          </p>

          <p><strong>Sentence multiplier:</strong> {{ modelMeta.sentence_multiplier }}</p>

          <details>
            <summary>Show weights used</summary>
            <pre>{{ modelMeta.weights_used }}</pre>
          </details>
        </div>
        <div style="margin: 2rem 0" v-else>No previous model</div>

        <div style="display: flex; flex-direction: row">
          <div style="display: flex; flex-direction: column" v-if="!isLoading">
            <button
              class="btn-default"
              style="width: 20rem"
              @click="trainModel"
              :disabled="
                !themeForTraining.learning || themeForTraining.learning.total_snapshots < 1
              "
            >
              <VisibleText v-if="hasModel">Re-train model with snapshots</VisibleText>
              <VisibleText v-else>Train first model with snapshots</VisibleText>
            </button>
            <div
              style="margin: auto 1rem"
              v-if="!themeForTraining.learning || themeForTraining?.learning.total_snapshots < 1"
            >
              You need snapshots to train a model.
            </div>
            <div style="margin: auto 1rem" v-else>
              Snapshots: {{ themeForTraining.learning.total_snapshots }}
            </div>
          </div>

          <button
            class="btn-default"
            style="
              width: 20rem;
              margin: 0 2rem;
              height: 2rem;
              background-color: var(--error-text-colour);
            "
            @click="deleteModel"
            v-if="hasModel && !isLoading"
          >
            <VisibleText>Delete model</VisibleText>
            <SvgIconMeaningful
              style="padding: 0; height: 1rem; margin: auto"
              class="complete-icon-svg"
              icon="deleteIcon"
              title="delete this model"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- TAB 2: ADVANCED -->
    <div v-if="activeTab === 'advanced'" class="tab-content">
      <h4>Customize Training Weights</h4>
      <span
        style="height: 2rem; width: 2rem; margin-top: 1rem"
        class="also-found__loading-spinner spinner"
        v-if="isLoading"
      ></span>
      <p class="hint">Optional. Defaults are safe and recommended.</p>

      <div v-for="(value, key) in weights" :key="key" class="weight-row">
        <label :for="key">{{ weightLabels[key] }}</label>

        <input type="range" :id="key" min="0" max="5" step="0.1" v-model.number="weights[key]" />

        <span class="weight-value">{{ weights[key].toFixed(1) }}</span>
      </div>

      <button class="btn-secondary" @click="resetWeights">Reset to default weights</button>

      <div style="display: flex; flex-direction: row">
        <div style="display: flex; flex-direction: column" v-if="!isLoading">
          <button
            class="btn-default"
            style="width: 20rem"
            @click="trainModel"
            :disabled="!themeForTraining.learning || themeForTraining.learning.total_snapshots < 1"
          >
            <VisibleText v-if="hasModel">Re-train model with snapshots</VisibleText>
            <VisibleText v-else>Train first model with snapshots</VisibleText>
          </button>
          <div
            style="margin: auto 1rem"
            v-if="!themeForTraining.learning || themeForTraining?.learning.total_snapshots < 1"
          >
            You need snapshots to train a model.
          </div>
          <div style="margin: auto 1rem" v-else>
            Snapshots: {{ themeForTraining.learning.total_snapshots }}
          </div>
        </div>

        <button
          class="btn-default"
          style="
            width: 20rem;
            margin: 0 2rem;
            height: 2rem;
            background-color: var(--error-text-colour);
          "
          @click="deleteModel"
          v-if="hasModel && !isLoading"
        >
          <VisibleText>Delete model</VisibleText>
          <SvgIconMeaningful
            style="padding: 0; height: 1rem; margin: auto"
            class="complete-icon-svg"
            icon="deleteIcon"
            title="delete this model"
          />
        </button>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from '@/components/UI/Modal.vue';
import SvgIconMeaningful from '@/components/UI/Svg/SvgIconMeaningful.vue';
import moment from 'moment';

import customTopicModelService from '@/services/customTopicModelService.js';

export default {
  name: 'TrainModel',
  props: {
    show: {
      default: () => false,
      type: Boolean,
    },
    themeForTraining: {
      default: () => {},
      type: Object,
    },
    trainedModel: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    Modal,
    SvgIconMeaningful,
  },
  data() {
    return {
      activeTab: 'train',

      CUSTOM_TOPIC_MODEL_SERVICE: new customTopicModelService(this.$pigeonline),

      modelMeta: {},

      DEFAULT_WEIGHTS: {
        human_dropped_ml_note: 4.0,
        human_reversed_ml_blocked_keywords: 4.0,
        human_blocked_keywords: 3.0,
        human_note_with_context: 3.2,
        human_note_without_context: 1.5,
        ml_added_note: 1.5,
        ml_blocked_keywords: 1.5,
        keyword_match: 2.0,
        no_match: 1.0,
      },

      weightLabels: {
        human_dropped_ml_note: 'Human deleted ML note',
        human_reversed_ml_blocked_keywords: 'Human reversed ML drop',
        human_blocked_keywords: 'Human dropped keyword match',
        human_note_with_context: 'Human note (with text)',
        human_note_without_context: 'Human note (no text)',
        ml_added_note: 'ML added note',
        ml_blocked_keywords: 'ML dropped keyword match',
        keyword_match: 'Keyword match',
        no_match: 'No match',
      },

      weights: {},

      isLoading: false,
    };
  },

  computed: {
    formattedDate() {
      if (!this.modelMeta?.created_on) return 'Never';
      return moment(this.modelMeta.created_on).format('MMMM Do YYYY, h:mm a');
    },
    hasModel() {
      if (!this.modelMeta) return false;
      if (!Object.keys(this.modelMeta).length) return false;
      return true;
    },
  },

  mounted() {
    // Initialize weights from defaults
    this.weights = { ...this.DEFAULT_WEIGHTS };
    this.refreshModel();
  },

  methods: {
    refreshModel() {
      this.modelMeta = this.trainedModel.model;
      // preload weights if available
      if (this.modelMeta?.weights_used) {
        this.weights = { ...this.modelMeta.weights_used };
      } else {
        this.weights = { ...this.DEFAULT_WEIGHTS };
      }
    },

    resetWeights() {
      this.weights = { ...this.DEFAULT_WEIGHTS };
    },

    async trainModel() {
      this.isLoading = true;
      const theme = this.themeForTraining;
      const theme_id = theme._id.$oid || theme._id;

      const payload = {
        model_theme_id: theme_id,
        weights: { ...this.weights },
      };
      this.modelMeta = {};
      await this.CUSTOM_TOPIC_MODEL_SERVICE.trainCustomModel(payload);
      this.isLoading = false;
      this.$emit('model-trained', theme_id);
    },

    deleteModel() {
      if (!this.trainedModel.has_model) return;
      const model_id = this.trainedModel.model._id;
      if (!model_id) return;

      this.$emit('deleteMachineLearningModel', model_id);
    },
  },
  watch: {
    trainedModel: {
      immediate: true,
      handler(newVal) {
        this.modelMeta = newVal.model;

        // preload weights if available
        if (newVal?.model?.weights_used) {
          this.weights = { ...newVal.model.weights_used };
        } else {
          this.weights = { ...this.DEFAULT_WEIGHTS };
        }
      },
    },
  },
};
</script>

<style scoped>
.tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.tab-btn {
  padding: 0.5rem 1rem;
  background: #eee;
  border-radius: 6px;
  cursor: pointer;
  border: none;
}

.tab-btn.active {
  background: #4d51cb;
  color: white;
}

.tab-content {
  margin-top: 1rem;
}

.model-status {
  background: #f7f7ff;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.weight-row {
  display: grid;
  grid-template-columns: 1fr 2fr 50px;
  align-items: center;
  margin-bottom: 1rem;
}

.weight-value {
  text-align: right;
  font-weight: bold;
}

.hint {
  font-size: 0.9rem;
  opacity: 0.7;
  margin-bottom: 1rem;
}
</style>
